import { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  UtensilsCrossed, 
  Coffee, 
  Sun, 
  Moon, 
  Apple,
  Flame,
  Target,
  TrendingUp,
  ShoppingCart,
  CheckCircle,
  Loader2,
  CalendarDays,
  Link as LinkIcon
} from "lucide-react";
import Navigation from "./Navigation";
import { clientApi, Client, dietPlanApi, DietPlan, DietPlanDay, DietPlanMeal } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";

const DietPage = () => {
  console.log('DietPage component rendering...');
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, clientName, isLoading } = useAuth();
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dietPlans, setDietPlans] = useState<DietPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<DietPlan | null>(null);
  // Index into the resolved `effectiveDays` array (see useMemo below).
  // -1 means "no day selected yet" (e.g. while loading); auto-corrected below.
  const [selectedDayIndex, setSelectedDayIndex] = useState<number>(0);

  // Saturday-first week to mirror the doctor-side editor. Used to highlight
  // "Today" if a matching day exists by name.
  const WEEKDAY_NAMES = useMemo(
    () => ['Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    []
  );

  // Today's name in our Saturday-first ordering. JS getDay() is 0=Sunday,
  // 1=Monday, ..., 6=Saturday â€” translate into our list.
  const todayName = useMemo(() => {
    const sundayFirst = new Date().getDay(); // 0..6
    // Map 0(Sun)..6(Sat) -> Saturday-first index
    // Sat=6 -> 0, Sun=0 -> 1, Mon=1 -> 2, ..., Fri=5 -> 6
    const idx = sundayFirst === 6 ? 0 : sundayFirst + 1;
    return WEEKDAY_NAMES[idx];
  }, [WEEKDAY_NAMES]);

  // Get client name from authentication context
  const getClientName = () => {
    return clientName; // Use the authenticated client name from AuthContext
  };

  // Redirect to auth if not authenticated (only after loading is complete)
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Fetch client data and their BMR diet plan
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    
    console.log('DietPage useEffect running...');
    const clientName = getClientName();
    console.log('Client name:', clientName);
    
    if (!clientName) {
      setError('No client information found');
      setLoading(false);
      return;
    }

    setLoading(true);
    clientApi.getAll()
      .then(clients => {
        console.log('Clients fetched:', clients.length);
        const foundClient = clients.find(c => 
          c.name.trim().toLowerCase() === clientName.trim().toLowerCase()
        );
        
        if (foundClient) {
          console.log('Client found:', foundClient.name);
          setClient(foundClient);
          
          // Fetch the client's diet plans
          return dietPlanApi.getAll(foundClient._id);
        } else {
          console.log('Client not found');
          setError('Client not found');
          return Promise.resolve([]);
        }
      })
      .then(plans => {
        console.log('Diet plans fetched:', plans.length);
        // The client only ever sees plans the doctor marked as active. If none
        // are explicitly active (e.g. legacy data), fall back to showing all so
        // the client is never left with a blank diet.
        const activePlans = plans.filter(p => p.status === 'active');
        const visiblePlans = activePlans.length > 0 ? activePlans : plans;
        setDietPlans(visiblePlans);

        if (visiblePlans.length > 0) {
          const sortedPlans = [...visiblePlans].sort((a, b) =>
            new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime()
          );
          setSelectedPlan(sortedPlans[0]);
        }
      })
      .catch(err => {
        console.error('Error fetching client or diet plans:', err);
        setError('Failed to load client data');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isAuthenticated, clientName]);

  // Get nutrition targets from client's BMR diet plan
  const getNutritionTargets = () => {
    if (!client || !client.diet || client.diet.length === 0) {
      // Fallback to default values
      return {
    calories: 1850,
    protein: 140,
    carbs: 200,
    fat: 65
  };
    }

    // Get the most recent BMR diet plan
    const sortedPlans = client.diet.sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    const latestPlan = sortedPlans[0];

    return {
      calories: latestPlan.calories || 1850,
      protein: latestPlan.protein || 140,
      carbs: latestPlan.carbs || 200,
      fat: latestPlan.fats || 65
    };
  };

  const dailyTargets = getNutritionTargets();

  // RTL helpers for Arabic lines (ensure numbers at start appear on the right)
  const containsArabic = (text: string) => /[\u0600-\u06FF]/.test(text);
  const needsRTL = (text: string) => containsArabic(text) || (/^\d/.test(text) && containsArabic(text));

  // Build the per-day view of the plan with full template/linked semantics:
  //   - Day 0 is always the template.
  //   - Days flagged isLinked (or with empty meals[]) inherit the template's
  //     meals at render time â€” same logic as the doctor-side editor.
  //   - Legacy plans with no `days` get wrapped into a single "Day 1".
  const effectiveDays = useMemo<{
    name: string;
    meals: DietPlanMeal[];
    state: 'template' | 'linked' | 'custom';
  }[]>(() => {
    if (!selectedPlan) return [];

    if (selectedPlan.days && selectedPlan.days.length > 0) {
      const templateMeals = selectedPlan.days[0]?.meals ?? [];
      return selectedPlan.days.map((d, idx) => {
        const isTemplate = idx === 0;
        const linked = !isTemplate && (d.isLinked || (d.meals?.length ?? 0) === 0);
        return {
          name: d.name || `Day ${idx + 1}`,
          meals: linked ? templateMeals : (d.meals ?? []),
          state: isTemplate ? 'template' : (linked ? 'linked' : 'custom'),
        };
      });
    }

    // Legacy: flat meals[] -> single Day 1
    if (selectedPlan.meals && selectedPlan.meals.length > 0) {
      return [{ name: 'Day 1', meals: selectedPlan.meals, state: 'template' }];
    }

    return [];
  }, [selectedPlan]);

  // Whenever the plan changes, snap selectedDayIndex into a sensible default:
  //   - If today's weekday matches a day in the plan, jump there.
  //   - Otherwise pick day 0.
  useEffect(() => {
    if (effectiveDays.length === 0) {
      setSelectedDayIndex(0);
      return;
    }
    const todayIdx = effectiveDays.findIndex(d => d.name === todayName);
    setSelectedDayIndex(todayIdx >= 0 ? todayIdx : 0);
  }, [effectiveDays, todayName]);

  // The day currently being viewed (clamped so we never index out of range).
  const activeDay = effectiveDays[Math.min(selectedDayIndex, effectiveDays.length - 1)] ?? null;
  const resolvedMeals: DietPlanMeal[] = activeDay?.meals ?? [];

  // Per-day nutrition totals for the selected day. Old code summed across the
  // legacy flat meals[] â€” now we sum the resolved meals for the active day.
  const currentIntake = useMemo(() => {
    if (resolvedMeals.length === 0) {
      return { calories: 0, protein: 0, carbs: 0, fat: 0 };
    }
    let totalCalories = 0;
    let totalProtein = 0;
    let totalCarbs = 0;
    let totalFat = 0;
    resolvedMeals.forEach(meal => {
      meal.foods.forEach(food => {
        const quantity = food.quantity || 0;
        const multiplier = quantity / 100; // foods store nutrition per 100g
        totalCalories += (food.calories || 0) * multiplier;
        totalProtein += (food.protein || 0) * multiplier;
        totalCarbs += (food.carbs || 0) * multiplier;
        totalFat += (food.fat || 0) * multiplier;
      });
    });
    return {
      calories: Math.round(totalCalories),
      protein: Math.round(totalProtein * 10) / 10,
      carbs: Math.round(totalCarbs * 10) / 10,
      fat: Math.round(totalFat * 10) / 10,
    };
  }, [resolvedMeals]);

  // Grocery list aggregates uniquely across the WHOLE plan (every day's
  // resolved meals) â€” that's what a client actually shops for.
  const groceryList = useMemo(() => {
    if (effectiveDays.length === 0) return [];
    const allFoods = effectiveDays.flatMap(d =>
      d.meals.flatMap(m => m.foods.map(f => f.name))
    );
    return [...new Set(allFoods)];
  }, [effectiveDays]);

  const getMealIcon = (mealName: string) => {
    const name = mealName.toLowerCase();
    if (name.includes('breakfast') || name.includes('morning')) return Coffee;
    if (name.includes('lunch') || name.includes('noon')) return Sun;
    if (name.includes('dinner') || name.includes('evening')) return Moon;
    if (name.includes('snack') || name.includes('snacks')) return Apple;
    return UtensilsCrossed;
  };

  const getProgressPercentage = (current: number, target: number) => {
    return Math.min((current / target) * 100, 100);
  };

  const getProgressColor = (current: number, target: number) => {
    const percentage = (current / target) * 100;
    if (percentage >= 90) return "bg-success";
    if (percentage >= 70) return "bg-warning";
    return "bg-primary";
  };

  return (
    <div className="min-h-screen bg-background ">
      <Navigation />
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2 fade-in">Diet Plan</h1>
          <p className="text-muted-foreground">
            {loading ? 'Loading your personalized nutrition program...' : 
             error ? 'Error loading nutrition data' :
             client ? `Personalized nutrition program for ${client.name}` :
             'Your personalized nutrition program'}
          </p>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-2">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
              <span className="text-muted-foreground">Loading your nutrition data...</span>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 mb-8">
            <div className="flex items-center space-x-2 text-destructive">
              <span className="text-sm font-medium">{error}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Using default nutrition targets. Please contact your nutritionist to set up your personalized plan.
            </p>
          </div>
        )}

        {/* Always show some content even if there are errors */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {/* Calories Card */}
          <Card className="card-wellness bounce-in overflow-hidden border-0 shadow-lg bg-gradient-to-br from-orange-50 to-red-50 hover:shadow-xl transition-all duration-300">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-orange-400 to-red-500 flex items-center justify-center shadow-md">
                    <Flame className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">Calories</h3>
                    <p className="text-xs text-gray-500">{activeDay?.name ?? 'Daily Energy'}</p>
                  </div>
                </div>
              </div>
              <div className="text-center mb-4">
                <div className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                  {currentIntake?.calories || 0}
                </div>
                <div className="text-sm font-medium text-gray-600 mt-1">/ {dailyTargets.calories} kcal</div>
              </div>
              <div className="relative">
                <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-orange-400 to-red-500 rounded-full transition-all duration-500"
                    style={{ width: `${getProgressPercentage(currentIntake.calories, dailyTargets.calories)}%` }}
                  ></div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Protein Card */}
          <Card className="card-wellness bounce-in overflow-hidden border-0 shadow-lg bg-gradient-to-br from-purple-50 to-indigo-50 hover:shadow-xl transition-all duration-300" style={{ animationDelay: "0.1s" }}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-400 to-indigo-500 flex items-center justify-center shadow-md">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">Protein</h3>
                    <p className="text-xs text-gray-500">Muscle Building</p>
                  </div>
                </div>
              </div>
              <div className="text-center mb-4">
                <div className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                  {currentIntake?.protein || 0}
                </div>
                <div className="text-sm font-medium text-gray-600 mt-1">/ {dailyTargets.protein} g</div>
              </div>
              <div className="relative">
                <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-purple-400 to-indigo-500 rounded-full transition-all duration-500"
                    style={{ width: `${getProgressPercentage(currentIntake.protein, dailyTargets.protein)}%` }}
                  ></div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Carbs Card */}
          <Card className="card-wellness bounce-in overflow-hidden border-0 shadow-lg bg-gradient-to-br from-yellow-50 to-orange-50 hover:shadow-xl transition-all duration-300" style={{ animationDelay: "0.2s" }}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 flex items-center justify-center shadow-md">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">Carbs</h3>
                    <p className="text-xs text-gray-500">Energy Source</p>
                  </div>
                </div>
              </div>
              <div className="text-center mb-4">
                <div className="text-4xl font-bold bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">
                  {currentIntake?.carbs || 0}
                </div>
                <div className="text-sm font-medium text-gray-600 mt-1">/ {dailyTargets.carbs} g</div>
              </div>
              <div className="relative">
                <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full transition-all duration-500"
                    style={{ width: `${getProgressPercentage(currentIntake.carbs, dailyTargets.carbs)}%` }}
                  ></div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Fat Card */}
          <Card className="card-wellness bounce-in overflow-hidden border-0 shadow-lg bg-gradient-to-br from-pink-50 to-rose-50 hover:shadow-xl transition-all duration-300" style={{ animationDelay: "0.3s" }}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-pink-400 to-rose-500 flex items-center justify-center shadow-md">
                    <Apple className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">Fat</h3>
                    <p className="text-xs text-gray-500">Essential Fats</p>
                  </div>
                </div>
              </div>
              <div className="text-center mb-4">
                <div className="text-4xl font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">
                  {currentIntake?.fat || 0}
                </div>
                <div className="text-sm font-medium text-gray-600 mt-1">/ {dailyTargets.fat} g</div>
              </div>
              <div className="relative">
                <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-pink-400 to-rose-500 rounded-full transition-all duration-500"
                    style={{ width: `${getProgressPercentage(currentIntake.fat, dailyTargets.fat)}%` }}
                  ></div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {!loading && !error && (
          <>
            {/* BMR Calculation Info */}
            {/* {client && client.diet && client.diet.length > 0 && (
              <div className="mb-8">
                <Card className="card-wellness">
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Target className="w-4 h-4 text-primary" />
                      <span className="text-sm font-medium text-muted-foreground">Your Nutrition Calculation</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                      {(() => {
                        const latestPlan = client.diet.sort((a, b) => 
                          new Date(b.date).getTime() - new Date(a.date).getTime()
                        )[0];
                        return (
                          <>
                            <div>
                              <span className="text-muted-foreground">BMR:</span>
                              <span className="ml-1 font-medium">{latestPlan.bmr} cal/day</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Activity Level:</span>
                              <span className="ml-1 font-medium">{latestPlan.activityLevel}x</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Equation:</span>
                              <span className="ml-1 font-medium">{latestPlan.equationMethod}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Calculated:</span>
                              <span className="ml-1 font-medium">{new Date(latestPlan.date).toLocaleDateString()}</span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )} */}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Meal Plan */}
          <div className="xl:col-span-2">
            <Card className="card-wellness slide-up">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <UtensilsCrossed className="w-5 h-5 text-primary" />
                  <span>
                    {activeDay
                      ? (activeDay.name === todayName
                          ? `Today's Meal Plan Â· ${activeDay.name}`
                          : `${activeDay.name} Meal Plan`)
                      : "Meal Plan"}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                      {!selectedPlan ? (
                        <div className="text-center py-8">
                          <UtensilsCrossed className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-muted-foreground mb-2">
                            No Diet Plan Available
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {dietPlans.length === 0 
                              ? "No meal-based diet plans have been created for you yet. Please contact your nutritionist to set up your personalized meal plan."
                              : "Please select a diet plan to view your meals."
                            }
                          </p>
                        </div>
                      ) : (
                        <>
                          {/* Plan Selector if multiple plans exist */}
                          {dietPlans.length > 1 && (
                            <div className="mb-4">
                              <label className="text-sm font-medium text-muted-foreground mb-2 block">
                                Select Diet Plan:
                              </label>
                              <select 
                                value={selectedPlan._id || ''} 
                                onChange={(e) => {
                                  const plan = dietPlans.find(p => p._id === e.target.value);
                                  if (plan) setSelectedPlan(plan);
                                }}
                                className="w-full p-2 border rounded-md bg-background"
                              >
                                {dietPlans.map(plan => (
                                  <option key={plan._id} value={plan._id}>
                                    {plan.name} {plan.status === 'active' ? '(Active)' : ''}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}

                          {/* Per-day picker: only render when plan has > 1 day */}
                          {effectiveDays.length > 1 && (
                            <div className="mb-6">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center space-x-2">
                                  <CalendarDays className="w-4 h-4 text-primary" />
                                  <span className="text-sm font-semibold">Day</span>
                                </div>
                                {activeDay?.state === 'linked' && (
                                  <div className="flex items-center text-[11px] text-muted-foreground">
                                    <LinkIcon className="w-3 h-3 mr-1" />
                                    Mirrors {effectiveDays[0]?.name}
                                  </div>
                                )}
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2">
                                {effectiveDays.map((d, dIdx) => {
                                  const isSelected = dIdx === selectedDayIndex;
                                  const isToday = d.name === todayName;
                                  // Build the visual style. Today gets a green
                                  // identity in every state so the client can
                                  // always spot it at a glance.
                                  const base = "group relative flex flex-col items-start text-left px-2.5 py-2.5 rounded-xl border-2 transition-all duration-200 min-w-0";
                                  const stateClasses = isSelected
                                    ? (isToday
                                        ? "bg-green-600 text-white border-green-600 shadow-md"
                                        : "bg-primary text-primary-foreground border-primary shadow-md")
                                    : (isToday
                                        ? "bg-green-50 text-green-800 border-green-500 hover:bg-green-100"
                                        : "bg-white text-foreground border-gray-200 hover:border-gray-300 hover:bg-gray-50");

                                  const subTextCls = isSelected
                                    ? (isToday ? "text-white/85" : "text-primary-foreground/80")
                                    : (isToday ? "text-green-700/80" : "text-muted-foreground");

                                  const dotCls = isSelected
                                    ? (isToday ? "bg-white" : "bg-primary-foreground")
                                    : (isToday ? "bg-green-500" : "bg-gray-300");

                                  return (
                                    <button
                                      key={`${d.name}-${dIdx}`}
                                      type="button"
                                      onClick={() => setSelectedDayIndex(dIdx)}
                                      className={`${base} ${stateClasses}`}
                                    >
                                      <div className="flex items-center justify-between w-full">
                                        <span className={`text-[9px] font-bold tracking-wider uppercase ${subTextCls}`}>
                                          {d.name.slice(0, 3)}
                                        </span>
                                        <span className={`w-1.5 h-1.5 rounded-full ${dotCls}`}></span>
                                      </div>
                                      <span className="font-bold text-[13px] mt-1 leading-tight w-full truncate">
                                        {d.name}
                                      </span>
                                      <span className={`text-[10px] mt-1 font-medium ${subTextCls}`}>
                                        {d.meals.length} {d.meals.length === 1 ? 'meal' : 'meals'}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {resolvedMeals.length === 0 && (
                            <div className="text-center py-8 border rounded-lg">
                              <UtensilsCrossed className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                              <p className="text-sm text-muted-foreground">
                                No meals scheduled for {activeDay?.name ?? 'this day'} yet.
                              </p>
                            </div>
                          )}

                          {resolvedMeals.map((meal, index) => {
                            const Icon = getMealIcon(meal.name);
                            const mealId = `meal-${index}`;
                            
                            // Calculate meal calories (only from main foods)
                            const mealCalories = meal.foods.reduce((total, food) => {
                              return total + ((food.calories || 0) * (food.quantity || 0) / 100);
                            }, 0);
                    
                    return (
                      <div 
                                key={index}
                                className={`p-6 rounded-lg border transition-all duration-200`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <Icon className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                                      <h3 className={`font-semibold`}>
                                {meal.name}
                              </h3>
                                      <p className="text-sm text-muted-foreground">
                                        {meal.foods.length} main food items
                                      </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-4">
                                    <Badge variant="outline">{Math.round(mealCalories)} cal</Badge>
                          </div>
                        </div>
                        
                        <ul className="space-y-2 text-sm text-muted-foreground">
                          {meal.foods.map((food, foodIndex) => (
                            <li key={foodIndex} className="flex items-center space-x-2">
                              <div className="w-1 h-1 bg-primary rounded-full"></div>
                              <span
                                dir={needsRTL(food.name) ? 'rtl' : undefined}
                                className={needsRTL(food.name) ? 'text-right' : undefined}
                                style={needsRTL(food.name) ? { unicodeBidi: 'plaintext' as any } : undefined}
                              >
                                {food.name}
                                {food.quantity && food.unit && (
                                  <span className="text-xs opacity-70 ml-1">
                                    ({food.quantity}{food.unit})
                                  </span>
                                )}
                              </span>
                            </li>
                          ))}
                        </ul>
                        
                        {/* Show replacement options if they exist in notes */}
                        {meal.notes && meal.notes.includes('Replacement options:') && (
                          <div className="mt-4 pt-4 border-t border-dashed">
                            <div className="flex items-center space-x-2 mb-2">
                              <div className="w-1 h-1 bg-orange-500 rounded-full"></div>
                              <span className="text-xs font-medium text-orange-600">Replacement Options:</span>
                            </div>
                            <ul className="space-y-1 text-xs text-muted-foreground ml-3">
                              {(() => {
                                // Parse replacement options with sophisticated comma handling
                                const replacementText = meal.notes.replace('Replacement options: ', '');
                                console.log('DietPage parsing replacement text:', replacementText);
                                const foods = [];
                                let currentFood = '';
                                let inParens = false;
                                let parensCount = 0;
                                
                                for (let i = 0; i < replacementText.length; i++) {
                                  const char = replacementText[i];
                                  
                                  if (char === '(') {
                                    inParens = true;
                                    parensCount++;
                                  } else if (char === ')') {
                                    parensCount--;
                                    if (parensCount === 0) {
                                      inParens = false;
                                    }
                                  } else if (char === ',' && !inParens) {
                                    // Only split on comma if we're not inside parentheses
                                    if (currentFood.trim()) {
                                      foods.push(currentFood.trim());
                                      currentFood = '';
                                      continue;
                                    }
                                  }
                                  
                                  currentFood += char;
                                }
                                
                                // Add the last food if there's any
                                if (currentFood.trim()) {
                                  foods.push(currentFood.trim());
                                }
                                
                                console.log('DietPage parsed foods:', foods);
                                
                                return foods.map((food, foodIndex) => (
                                  <li key={foodIndex} className="flex items-center space-x-2">
                                    <div className="w-1 h-1 bg-orange-400 rounded-full"></div>
                                    <span
                                      dir={needsRTL(food) ? 'rtl' : undefined}
                                      className={needsRTL(food) ? 'text-right' : undefined}
                                      style={needsRTL(food) ? { unicodeBidi: 'plaintext' as any } : undefined}
                                    >
                                      {food}
                                    </span>
                                  </li>
                                ));
                              })()}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })}
                        </>
                      )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Grocery List */}
          <div>
            <Card className="card-wellness slide-up" style={{ animationDelay: "0.2s" }}>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <ShoppingCart className="w-5 h-5 text-primary" />
                  <span>Grocery List</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-6">
                  {groceryList.map((item, index) => (
                    <div key={index} className="flex items-center space-x-3 p-2 rounded hover:bg-secondary/50 transition-colors">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </div>
                <Button className="w-full bg-accent text-accent-foreground" variant="default">
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Export to Shopping App
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DietPage;